import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { poiPlacePayload } from '@/features/locations/utils/mixpanelEvents';
import { placeToPointOfInterest } from '@/features/locations/utils/placePayload';
import type { PointOfInterestPlace } from '@/features/locations/utils/pointOfInterest';
import { useActiveWorkspace } from '@/features/workspaces/hooks/useActiveWorkspace';
import {
  type ILocationMarkersCache,
  useAddWorkspaceLocations,
  workspacesKeyFactory,
} from '@/features/workspaces/services/workspace.api';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import type { Coordinates } from '../context/ManualPinContext';
import { useManualPin } from '../context/ManualPinContext';
import { useComputePreferences } from './useComputePreferences';
import type { AddressLines } from '@/features/mapbox/utils/featureAddressLines';

/**
 * Launches an independent backend compute run for ONE manually-placed
 * location — a search result or a map click while the add-pin tool is
 * armed. There is no staging/review step: each call fires its own
 * `useAddWorkspaceLocations` mutation immediately, using the saved compute
 * preferences (radius + metrics), and hands the resulting run to
 * `registerComputeRun` so the surface follows it over SSE — the same
 * mechanism a batch save used to feed, just for a single location.
 *
 * `onLaunched` fires immediately with the tempId so the panel/camera can
 * react before the backend compute finishes.
 *
 * No-op when there is no active workspace (mirrors the pre-instant-launch
 * guard). No shared in-flight guard: each call is independent, so rapid
 * successive calls (e.g. several quick clicks) each fire their own mutation.
 */
export interface LaunchLocationComputeMeta {
  /** How the location was placed. A pin dropped from a place card reports `poi` in Mixpanel. */
  source: 'search' | 'drop_pin';
  /** Hunting zone under the placed point, or null — see `zoneIdAtLngLat`. */
  zoneId: string | null;
  /**
   * The address a search result already resolved. Sent with the point so the backend keeps it
   * instead of geocoding the same address again. Absent for a dropped pin, which the backend
   * resolves from the point.
   */
  addressLines?: AddressLines | null;
  /**
   * The place this pin is dropped from, as the basemap gave it. Sent with the location so the
   * stored row keeps the link. Absent for a search result or a plain drop.
   */
  place?: PointOfInterestPlace;
}

export function useLaunchLocationCompute(onLaunched: (locationId: string) => void) {
  const { activeId } = useActiveWorkspace();
  const { preferences } = useComputePreferences();
  const { mutate } = useAddWorkspaceLocations();
  const { markPending, clearPending, registerComputeRun } = useManualPin();
  const trackAction = useMixpanel((s) => s.trackAction);
  const queryClient = useQueryClient();

  return useCallback(
    (coords: Coordinates, meta: LaunchLocationComputeMeta) => {
      if (!activeId) return;

      const workspaceId = activeId;
      const tempId = crypto.randomUUID();

      // Snapshot the workspace's markers BEFORE the optimistic insert below
      // writes this launch's tempId marker into the same cache — this is the
      // "before" set `useWorkspaceCompute` excludes when resolving the real id
      // by coordinates on completion, so recomputing at an already-added
      // address (coincident coordinates, brand-new location id) can't
      // resolve to the pre-existing location instead of the new one.
      const existingMarkerIds =
        queryClient
          .getQueryData<ILocationMarkersCache>(
            workspacesKeyFactory.locationMarkers(workspaceId).queryKey,
          )
          ?.markers.map((m) => m.id) ?? [];

      onLaunched(tempId);
      // Mark computing from this instant — the add mutation is still in
      // flight, so tempId isn't a registered ComputeRun yet. Without this,
      // the panel's results query briefly enables for a tempId the backend
      // has never heard of, 404s, and its error handler clears the selection.
      markPending([tempId]);

      mutate(
        {
          workspaceId,
          data: {
            metrics: [...preferences.metrics],
            locations: [
              {
                coordinates: coords,
                radius: preferences.radiusMeters,
                ...(meta.addressLines ?? {}),
                ...(meta.place ? { point_of_interest: placeToPointOfInterest(meta.place) } : {}),
              },
            ],
          },
          tempIds: [tempId],
        },
        {
          onSuccess: (project) => {
            registerComputeRun({
              projectId: project.id,
              workspaceId,
              tempIds: [tempId],
              autoSelectSingle: true,
              coordinates: coords,
              existingMarkerIds,
            });
            trackAction(ACTION_TRACKED.LOCATIONS_ADDED, {
              count: 1,
              source: meta.place ? 'poi' : meta.source,
              zone_id: meta.zoneId,
              ...(meta.place ? poiPlacePayload(meta.place) : {}),
            });
          },
          onError: () => {
            clearPending([tempId]);
          },
        },
      );
    },
    [
      activeId,
      preferences,
      mutate,
      markPending,
      clearPending,
      registerComputeRun,
      trackAction,
      onLaunched,
      queryClient,
    ],
  );
}
